import React, { Fragment as _, useContext } from 'react';
import { Box } from 'theme-ui';
import { AssessmentQuestionContext } from './AssessmentQuestion';
import { interactionContext as InteractionContext } from './interactionContext';
import { getButtonFocus } from 'styles/variables';
import hexToRGB from 'services/hexToRGB';

const CheckboxRadioBtn = props => {
  const assessmentQuestionContext = useContext<any>(AssessmentQuestionContext);
  const { singleSelect, selected, setSelected } = assessmentQuestionContext;
  const { id, color, selectedColor } = props;

  return (
    <Box
      id={`checkbox-radio-${id}`}
      sx={{
        width: 20,
        height: 20,
        background: selected.includes(id) ? selectedColor : color,
        borderRadius: singleSelect ? '50%' : 0,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
        transition: 'background, box-shadow 0.15s',

        cursor: 'pointer',
        ':hover': {
          boxShadow: '0 0 3px 2px rgba(0,0,0,0.15)',
        },

        pointerEvents: window.dev() ? 'none' : 'auto',
      }}
    ></Box>
  );
};

const AssessmentQuestionOption = ({ children, ...props }) => {
  const assessmentQuestionContext = useContext<any>(AssessmentQuestionContext);
  const interactionContext = useContext<any>(InteractionContext);

  const {
    singleSelect,
    selected,
    setSelected,
    isLocked,
  } = assessmentQuestionContext;

  const {
    optionBackgroundColor = 'fff',
    dropShadow = true,
    optionCheckboxColor,
    optionCheckboxSelectedColor,
    optionBorderRadius = 10,
  } = interactionContext.props;

  const { id, qNum, numOptions, index } = props;

  const lastOptionIsOddIndex = index === numOptions - 1 && !!(numOptions % 2);
  let marginX = lastOptionIsOddIndex ? 'noMargin' : index % 2;
  //

  const margin = {
    0: '10px 20px 10px 0',
    1: '10px 0 10px 20px',
    noMargin: '10px 0',
  }[marginX];

  // TODO - extract this
  let optionBackgroundColorIsWhite: number[] | boolean = Object.values(
    hexToRGB(optionBackgroundColor) || {}
  );

  if (optionBackgroundColorIsWhite.length) {
    optionBackgroundColorIsWhite = optionBackgroundColorIsWhite.every(
      entry => entry === 0
    );
  }

  const focus = getButtonFocus(
    optionBackgroundColorIsWhite ? '#000' : optionBackgroundColor
  ).replace('box-shadow: ', '');

  return (
    <Box
      className="chameleon-assessment-option"
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 50,
        width: '100%',
        maxWidth: 'calc(50% - 20px)',
        margin,
        padding: '0 0 0 10px',
        background: optionBackgroundColor,
        borderRadius: optionBorderRadius,
        boxShadow: dropShadow ? '0px 0px 6px 0px rgba(0,0,0,0.15)' : 'none',

        transition: 'box-shadow 0.1s',

        '&:focus-visible': {
          boxShadow: `${focus.trim()}`,
        },

        '@media screen and (max-width: 768px)': {
          maxWidth: '100%',
          margin: '10px 0 !important',
        },

        // we kill pointerEvents at the root
        // and enable for children below specifically
        // . . .
        pointerEvents: 'none',

        ...(isLocked && {
          '* :not([class*="TextToSpeech"])': {
            pointerEvents: 'none !important',
          },
        }),
      }}
      tabIndex={isLocked ? -1 : 0}
      onKeyDown={e => {
        if (window.dev()) {
          return;
        }

        // something something to prevent the window from scrolling
        // when using the spaceBar to select / deselect option
        if (e.keyCode === 32) {
          e.preventDefault();
        }
      }}
      onKeyUp={e => {
        if (e.target !== e.currentTarget) {
          return;
        }

        if (!['Enter', ' '].includes(e.key)) {
          return;
        }

        setSelected(id);
      }}
      onClick={() => {
        setSelected(id);
      }}
    >
      {!window.dev() && (
        <input
          id={`i${id}`}
          type={singleSelect ? 'radio' : 'checkbox'}
          value={`i${id}`}
          name={interactionContext.props.id}
          checked={(selected as any).includes(id)}
          readOnly
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            opacity: 0,
          }}
          tabIndex={-1}
          disabled={isLocked}
        />
      )}

      <label
        htmlFor={`i${id}`}
        style={{
          display: 'flex',
          flex: 1,
          pointerEvents: !window.dev() ? 'none' : 'auto',
        }}
        tabIndex={-1}
      >
        <Box
          sx={{
            flex: 1,
            padding: '12.5px 0 12.5px 10px',
            ...(window.dev() && {
              padding: '7.5px 0 5px 0px',
              '.chameleon-text': {
                padding: '5px 10px 5px 5px',
              },
            }),

            '[class*="TextToSpeech"]': {
              pointerEvents: 'auto',
            },
          }}
        >
          {children}
        </Box>
      </label>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: 50,
        }}
      >
        <CheckboxRadioBtn
          id={id}
          color={!!optionCheckboxColor ? optionCheckboxColor : '#fff'}
          selectedColor={
            !!optionCheckboxSelectedColor ? optionCheckboxSelectedColor : '#000'
          }
        />
      </Box>
    </Box>
  );
};

export default AssessmentQuestionOption;
